import {
  useCallback,
  useMemo,
  useState,
} from 'react';
import moment from 'moment-timezone';

import { combineDateAndTime, isToday } from '../utils/date';
import { buildRecurrenceRule, nextOccurrence } from '../utils/recurrence';

const useReminderForm = ({
  initialContent = '',
  initialSendAt,
  initialRecurrence,
} = {}) => {
  const [reminderText, setReminderText] = useState(initialContent);
  const [sendDate, setSendDate] = useState(() => (
    initialRecurrence?.anchorDate
      ? moment(initialRecurrence.anchorDate).local()
      : moment(initialSendAt).local()
  ));
  const [sendTime, setSendTime] = useState(() => moment(initialSendAt).local());
  const [isRecurring, setIsRecurring] = useState(Boolean(initialRecurrence));
  const [interval, setInterval] = useState(initialRecurrence?.interval || 1);
  const [byDay, setByDay] = useState(initialRecurrence?.byDay || []);
  const [endDate, setEndDate] = useState(() => (
    initialRecurrence?.endDate ? moment(initialRecurrence.endDate).local() : null
  ));

  // Preserve the stored tz for existing recurring docs so wall-clock fire time stays stable.
  const timeZone = initialRecurrence?.timeZone || moment.tz.guess();

  const buildRule = useCallback(() => buildRecurrenceRule({
    isRecurring, sendDate, sendTime, interval, byDay, endDate, timeZone,
  }), [isRecurring, sendDate, sendTime, interval, byDay, endDate, timeZone]);

  const nextFireMoment = useMemo(() => {
    if (!isRecurring || byDay.length === 0) return null;
    const rule = buildRule();
    if (!rule) return null;
    const pickedMs = combineDateAndTime(sendDate.clone(), sendTime).valueOf();
    return nextOccurrence({
      now: Math.max(pickedMs - 1, Date.now()),
      recurrence: rule,
    });
  }, [isRecurring, byDay, sendDate, sendTime, buildRule]);

  const minTime = useMemo(() => (
    !isRecurring && isToday(sendDate) ? moment().local() : null
  ), [sendDate, isRecurring]);

  const submitDisabled = (
    !reminderText
    || !sendDate
    || !sendTime
    || (isRecurring && (byDay.length === 0 || !nextFireMoment))
  );

  // combineDateAndTime mutates via startOf('day') — always pass a clone.
  const resolveSendAt = useCallback(() => {
    if (!sendDate || !sendTime) {
      return { rule: null, sendAt: null, isPastDate: false };
    }
    const pickedMs = combineDateAndTime(sendDate.clone(), sendTime).valueOf();
    const rule = buildRule();
    if (rule) {
      const next = nextOccurrence({
        now: Math.max(pickedMs - 1, Date.now()),
        recurrence: rule,
      });
      return { rule, sendAt: next ? next.valueOf() : null, isPastDate: false };
    }
    return { rule: null, sendAt: pickedMs, isPastDate: pickedMs <= Date.now() };
  }, [sendDate, sendTime, buildRule]);

  const reset = useCallback(() => {
    setReminderText('');
    setSendDate(moment().local());
    setSendTime(moment().local());
    setIsRecurring(false);
    setInterval(1);
    setByDay([]);
    setEndDate(null);
  }, []);

  return {
    reminderText,
    setReminderText,
    sendDate,
    setSendDate,
    sendTime,
    setSendTime,
    isRecurring,
    setIsRecurring,
    interval,
    setInterval,
    byDay,
    setByDay,
    endDate,
    setEndDate,
    timeZone,
    nextFireMoment,
    minTime,
    submitDisabled,
    resolveSendAt,
    reset,
  };
};

export default useReminderForm;
